import { useCallback, useEffect, useRef, useState } from 'react';

const OFFSET_DEFAULT = 8;

/**
 * @typedef {'top' | 'bottom' | 'left' | 'right' | 'top-start' | 'top-end' | 'bottom-start' | 'bottom-end'} Placement
 */

/**
 * @typedef {Object} PopoverPosition
 * @property {number} top
 * @property {number} left
 */

/**
 * @typedef {Object} PopoverOptions
 * @property {Placement} [placement='bottom']
 * @property {number} [offset=8]
 * @property {boolean} [closeOnOutsideClick=true]
 * @property {boolean} [closeOnEscape=true]
 * @property {boolean} [closeOnScroll=false]
 * @property {() => void} [onOpen]
 * @property {() => void} [onClose]
 */

/**
 * @typedef {Object} UsePopoverReturn
 * @property {boolean} isOpen
 * @property {() => void} open
 * @property {() => void} close
 * @property {() => void} toggle
 * @property {React.RefObject<HTMLElement>} triggerRef
 * @property {React.RefObject<HTMLElement>} popoverRef
 * @property {PopoverPosition} position
 * @property {boolean} positioned
 * @property {Placement} placement
 */

/**
 * @param {HTMLElement} trigger
 * @param {HTMLElement} popover
 * @param {Placement} placement
 * @param {number} offset
 * @returns {{ position: PopoverPosition, resolvedPlacement: Placement }}
 */
function getPosition(trigger, popover, placement, offset) {
  const triggerRect = trigger.getBoundingClientRect();
  const popoverRect = popover.getBoundingClientRect();
  const viewport = {
    width: window.innerWidth,
    height: window.innerHeight,
  };

  const placements = {
    top: {
      top: triggerRect.top - popoverRect.height - offset,
      left: triggerRect.left + triggerRect.width / 2 - popoverRect.width / 2,
    },
    'top-start': {
      top: triggerRect.top - popoverRect.height - offset,
      left: triggerRect.left,
    },
    'top-end': {
      top: triggerRect.top - popoverRect.height - offset,
      left: triggerRect.right - popoverRect.width,
    },
    bottom: {
      top: triggerRect.bottom + offset,
      left: triggerRect.left + triggerRect.width / 2 - popoverRect.width / 2,
    },
    'bottom-start': {
      top: triggerRect.bottom + offset,
      left: triggerRect.left,
    },
    'bottom-end': {
      top: triggerRect.bottom + offset,
      left: triggerRect.right - popoverRect.width,
    },
    left: {
      top: triggerRect.top + triggerRect.height / 2 - popoverRect.height / 2,
      left: triggerRect.left - popoverRect.width - offset,
    },
    right: {
      top: triggerRect.top + triggerRect.height / 2 - popoverRect.height / 2,
      left: triggerRect.right + offset,
    },
  };

  const fallbacks = {
    top: 'bottom', 'top-start': 'bottom-start', 'top-end': 'bottom-end',
    bottom: 'top', 'bottom-start': 'top-start', 'bottom-end': 'top-end',
    left: 'right',
    right: 'left',
  };

  let resolvedPlacement = placement;
  let pos = placements[placement];

  const wouldOverflow =
    pos.top < 0 ||
    pos.top + popoverRect.height > viewport.height ||
    pos.left < 0 ||
    pos.left + popoverRect.width > viewport.width;

  if (wouldOverflow && fallbacks[placement]) {
    const fallback = fallbacks[placement];
    const fallbackPos = placements[fallback];
    const fallbackOverflows =
      fallbackPos.top < 0 ||
      fallbackPos.top + popoverRect.height > viewport.height ||
      fallbackPos.left < 0 ||
      fallbackPos.left + popoverRect.width > viewport.width;

    if (!fallbackOverflows) {
      resolvedPlacement = fallback;
      pos = fallbackPos;
    }
  }

  // clamp to viewport
  pos.left = Math.max(8, Math.min(pos.left, viewport.width - popoverRect.width - 8));
  pos.top = Math.max(8, Math.min(pos.top, viewport.height - popoverRect.height - 8));

  return { position: pos, resolvedPlacement };
}

/**
 * A production-grade popover hook that manages open state, positioning,
 * flipping, and cleanup.
 *
 * @param {PopoverOptions} [options={}]
 * @returns {UsePopoverReturn}
 *
 * @example
 * // Toggle on button click
 * const { isOpen, toggle, triggerRef, popoverRef, position } = usePopover({ placement: 'bottom-start' });
 *
 * @example
 * // Open on input focus
 * const { isOpen, open, close, triggerRef, popoverRef, position } = usePopover({ placement: 'bottom-start' });
 * <input ref={triggerRef} onFocus={open} />
 */
export function usePopover({
  placement: preferredPlacement = 'bottom',
  offset = OFFSET_DEFAULT,
  closeOnOutsideClick = true,
  closeOnEscape = true,
  closeOnScroll = false,
  onOpen,
  onClose,
} = {}) {
  const [isOpen, setIsOpen] = useState(false);
  const [positioned, setPositioned] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const [placement, setPlacement] = useState(preferredPlacement);

  const triggerRef = useRef(null);
  const popoverRef = useRef(null);

  const [triggerWidth, setTriggerWidth] = useState(null);

  const updatePosition = useCallback(() => {
    if (!triggerRef.current || !popoverRef.current) return;
    const { position: pos, resolvedPlacement } = getPosition(
      triggerRef.current,
      popoverRef.current,
      preferredPlacement,
      offset
    );
    setPosition(pos);
    setPlacement(resolvedPlacement);
    setTriggerWidth(triggerRef.current.getBoundingClientRect().width);
    setPositioned(true);
  }, [preferredPlacement, offset]);

  const open = useCallback(() => {
    if (isOpen) return;
    setIsOpen(true);
    setPositioned(false);
    onOpen?.();
  }, [isOpen, onOpen]);

  const close = useCallback(() => {
    setIsOpen(false);
    onClose?.();
  }, [onClose]);

  const toggle = useCallback(() => {
    if (isOpen) close();
    else open();
  }, [isOpen, open, close]);

  // recompute position after open — rAF ensures popover is in DOM and has dimensions
  useEffect(() => {
    if (!isOpen) return;
    const id = requestAnimationFrame(updatePosition);
    return () => cancelAnimationFrame(id);
  }, [isOpen, updatePosition]);

  // outside click
  useEffect(() => {
    if (!isOpen || !closeOnOutsideClick) return;
    const handler = (e) => {
      if (
        popoverRef.current?.contains(e.target) ||
        triggerRef.current?.contains(e.target)
      ) return;
      close();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isOpen, closeOnOutsideClick, close]);

  // escape key
  useEffect(() => {
    if (!isOpen || !closeOnEscape) return;
    const handler = (e) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isOpen, closeOnEscape, close]);

  // scroll — reposition or close
  useEffect(() => {
    if (!isOpen) return;
    const handler = () => {
      if (closeOnScroll) close();
      else updatePosition();
    };
    window.addEventListener('scroll', handler, true);
    return () => window.removeEventListener('scroll', handler, true);
  }, [isOpen, closeOnScroll, close, updatePosition]);

  // resize — reposition if trigger or popover size changes
  useEffect(() => {
    if (!isOpen) return;
    const observer = new ResizeObserver(updatePosition);
    if (triggerRef.current) observer.observe(triggerRef.current);
    if (popoverRef.current) observer.observe(popoverRef.current);
    return () => observer.disconnect();
  }, [isOpen, updatePosition]);

  return {
    isOpen,
    open,
    close,
    toggle,
    triggerRef,
    triggerWidth,
    popoverRef,
    position,
    positioned,
    placement,
  };
}